import { Badge, Button, Divider, Flex } from 'antd';
import { AppstoreOutlined, BlockOutlined, BorderOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { useBreakpoint } from '@/shared/lib/hooks';
import { comboList, type HotkeyActionId } from '@/shared/lib/hotkeys';
import { HotkeyTip } from '@/shared/ui/hotkey';
import { MOBILE_TAB_BAR_HEIGHT, TASKBAR_Z_INDEX } from './zLayers';

export interface TaskbarItem {
  key: string;
  label: string;
  /** Есть ли в этом свёрнутом окне несохранённые изменения — точка на плашке, не разворачивая. */
  dirty: boolean;
  /** Номер окна среди всех открытых (по порядку открытия): `Ctrl+Alt+<номер>` выводит его вперёд. */
  number?: number;
}

/**
 * Панель управления окнами слева внизу (как панель задач в Windows).
 *
 * «Свернуть все»/«Каскадом»/«Сеткой» относятся к открытым (не свёрнутым) окнам и видны, пока
 * хотя бы одно такое есть. «Закрыть все» и плашки — к свёрнутым: клик по плашке разворачивает
 * окно обратно на то же место и в том же размере (окно не размонтировалось); «Закрыть все»
 * закрывает их все разом, но через обычное закрытие каждого окна (с проверкой несохранённых
 * изменений, см. FloatingWindow) — а не в обход неё.
 *
 * Телефон (< 768px): окна — листы на весь экран, поэтому панель — лента плашек свёрнутых окон над
 * нижней панелью вкладок (горизонтальная прокрутка, высота плашек 40px, слева «Закрыть все»), и
 * только когда ни один лист не открыт (иначе лента закрыла бы кнопки внизу листа; у листа есть
 * своё «Свернуть»). «Каскадом»/«Сеткой» и «Свернуть все» на телефоне не рисуются: раскладывать
 * нечего — лист всегда один на весь экран.
 */
export function WindowTaskbar({
  items,
  hasOpenWindows,
  onRestore,
  onCloseAll,
  onMinimizeAll,
  onCascadeAll,
  onTileAll,
}: {
  items: TaskbarItem[];
  hasOpenWindows: boolean;
  onRestore: (key: string) => void;
  onCloseAll: () => void;
  onMinimizeAll: () => void;
  onCascadeAll: () => void;
  onTileAll: () => void;
}) {
  const { isMobile } = useBreakpoint();
  if (!hasOpenWindows && items.length === 0) return null;
  if (isMobile) {
    if (hasOpenWindows || items.length === 0) return null;
    return <MobileTaskbar items={items} onRestore={onRestore} onCloseAll={onCloseAll} />;
  }
  return (
    <Flex
      data-testid="window-taskbar"
      align="center"
      gap={4}
      wrap
      justify="flex-start"
      style={{ position: 'fixed', left: 8, bottom: 8, maxWidth: 'calc(100vw - 16px)', zIndex: TASKBAR_Z_INDEX }}
    >
      {hasOpenWindows ? (
        <>
          <HotkeyTip catalogId="app.minimizeAll" label="Свернуть все окна" display="inline-flex">
            <Button size="small" icon={<MinusOutlined />} aria-label="Свернуть все окна" onClick={onMinimizeAll} style={BUTTON_STYLE}>
              Свернуть все
            </Button>
          </HotkeyTip>
          <HotkeyTip catalogId="app.cascade" label="Разложить окна каскадом" display="inline-flex">
            <Button size="small" icon={<BlockOutlined />} aria-label="Разложить окна каскадом" onClick={onCascadeAll} style={BUTTON_STYLE} />
          </HotkeyTip>
          <HotkeyTip catalogId="app.tile" label="Разложить окна сеткой" display="inline-flex">
            <Button size="small" icon={<AppstoreOutlined />} aria-label="Разложить окна сеткой" onClick={onTileAll} style={BUTTON_STYLE} />
          </HotkeyTip>
        </>
      ) : null}

      {hasOpenWindows && items.length > 0 ? <Divider type="vertical" style={{ margin: 0 }} /> : null}

      {items.length > 0 ? (
        <>
          <HotkeyTip catalogId="app.closeMinimized" label="Закрыть все свёрнутые окна" display="inline-flex">
            <Button size="small" icon={<CloseOutlined />} aria-label="Закрыть все свёрнутые окна" onClick={onCloseAll} style={BUTTON_STYLE}>
              Закрыть все
            </Button>
          </HotkeyTip>
          <Divider type="vertical" style={{ margin: 0 }} />
          {items.map(({ key, label, dirty, number }) => (
            <HotkeyTip
              key={key}
              label={`Развернуть: ${label}${dirty ? ' (есть несохранённые изменения)' : ''}`}
              combo={number !== undefined && number >= 1 && number <= 9 ? comboList(`app.taskbar${number}` as HotkeyActionId) : undefined}
              display="inline-flex"
            >
              <Button size="small" icon={<BorderOutlined />} onClick={() => onRestore(key)} style={{ ...BUTTON_STYLE, maxWidth: 240 }}>
                <Badge dot={dirty} offset={[2, 0]}>
                  <span style={{ display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                    {label}
                  </span>
                </Badge>
              </Button>
            </HotkeyTip>
          ))}
        </>
      ) : null}
    </Flex>
  );
}

/** Лента свёрнутых окон на телефоне: над нижней панелью вкладок и «домашней» полосой iPhone. */
function MobileTaskbar({
  items,
  onRestore,
  onCloseAll,
}: {
  items: TaskbarItem[];
  onRestore: (key: string) => void;
  onCloseAll: () => void;
}) {
  return (
    <div
      data-testid="window-taskbar"
      role="toolbar"
      aria-label="Свёрнутые окна"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: `calc(${MOBILE_TAB_BAR_HEIGHT}px + env(safe-area-inset-bottom, 0px))`,
        zIndex: TASKBAR_Z_INDEX,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px calc(8px + env(safe-area-inset-right, 0px)) 8px calc(8px + env(safe-area-inset-left, 0px))',
        background: '#fff',
        borderTop: '1px solid #d9d9d9',
        overflowX: 'auto',
        overscrollBehaviorX: 'contain',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      <Button
        icon={<CloseOutlined />}
        aria-label="Закрыть все свёрнутые окна"
        onClick={onCloseAll}
        style={{ ...MOBILE_CHIP_STYLE, position: 'sticky', left: 0, zIndex: 1 }}
      >
        Закрыть все
      </Button>
      {items.map(({ key, label, dirty }) => (
        <Button
          key={key}
          icon={<BorderOutlined />}
          aria-label={`Развернуть: ${label}${dirty ? ' (есть несохранённые изменения)' : ''}`}
          onClick={() => onRestore(key)}
          style={{ ...MOBILE_CHIP_STYLE, maxWidth: 220 }}
        >
          <Badge dot={dirty} offset={[2, 0]}>
            <span style={{ display: 'inline-block', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
              {label}
            </span>
          </Badge>
        </Button>
      ))}
    </div>
  );
}

const MOBILE_CHIP_STYLE: React.CSSProperties = { height: 40, flex: '0 0 auto', fontSize: 14 };

const BUTTON_STYLE: React.CSSProperties = { fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' };
